import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { DateTime } from 'luxon'

/** Shape of a failed Zod safeParse result (error.issues); path may be PropertyKey[] in Zod 4 */
export interface ValidationErrorResult {
  success: false
  error: { issues: Array<{ path: Array<string | number | symbol>; message: string }> }
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function buildValidationErrors(validatedData: ValidationErrorResult): Record<string, string> {
  const errors: Record<string, string> = {}
  for (const issue of validatedData.error.issues) {
    const key = issue.path[0] ?? '_'
    errors[String(key)] = issue.message
  }
  return errors
}

export function combineDateAndTime(date: Date, time: Date) {
  const result = new Date(date)

  result.setUTCHours(
    time.getUTCHours(),
    time.getUTCMinutes(),
    time.getUTCSeconds(),
    time.getUTCMilliseconds()
  )

  return result
}

export function getSettlementPeriod(referenceDate: Date) {
  const year = referenceDate.getFullYear()
  const month = referenceDate.getMonth() // 0-based

  // current period end = 24th of current month
  const periodEnd = new Date(year, month, 24)
  periodEnd.setHours(23, 59, 59, 999)

  // current period start = 26th of previous month
  const periodStart = new Date(year, month - 1, 26)
  periodStart.setHours(0, 0, 0, 0)

  return { periodStart, periodEnd }
}

export function getSettlementDate(referenceDate: Date) {
  const year = referenceDate.getFullYear()
  const month = referenceDate.getMonth()

  const settlementDate = new Date(year, month, 30)
  settlementDate.setHours(0, 0, 0, 0)

  return settlementDate
}

export function getRangeProgress(startDate: Date | string, endDate: Date | string, currentDate: Date | string = new Date()) {
  const start = new Date(startDate).getTime()
  const end = new Date(endDate).getTime()
  const current = new Date(currentDate).getTime()

  return (current - start) / (end - start)
}

export function checkQuarterPoints(startDate: Date | string, endDate: Date | string, currentDate: Date | string = new Date()): {
  isQuarter: boolean
  isHalf: boolean
  isThreeQuarter: boolean
  progress: number
} {
  const progress = getRangeProgress(startDate, endDate, currentDate)

  const epsilon = 0.01 // tolerance

  return {
    isQuarter: Math.abs(progress - 0.25) < epsilon,
    isHalf: Math.abs(progress - 0.5) < epsilon,
    isThreeQuarter: Math.abs(progress - 0.75) < epsilon,
    progress
  }
}

export function getBeginOfADate(date: string) {
  return new Date(`${date}T00:00:00.000Z`)
}

export function getEndOfADate(date: string) {
  return new Date(`${date}T23:59:59.999Z`)
}


export function combineSlotDateAndTimeInZone(
  date: Date,
  startTime: Date | string,
  timeZone: string
): Date {
  let hours: number
  let minutes: number
  let seconds: number

  if (startTime instanceof Date) {
    hours = startTime.getUTCHours()
    minutes = startTime.getUTCMinutes()
    seconds = startTime.getUTCSeconds()
  } else {
    const [h, m, s = '0'] = startTime.split(':')
    hours = Number(h)
    minutes = Number(m)
    seconds = Number(s)
  }

  const zoned = DateTime.fromObject(
    {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
      hour: hours,
      minute: minutes,
      second: seconds,
    },
    { zone: timeZone }
  )

  return zoned.toUTC().toJSDate()
}

export function combineSlotDateAndTime(date: Date, startTime: Date | string): Date {
  const result = new Date(date)

  if (startTime instanceof Date) {
    result.setHours(
      startTime.getHours(),
      startTime.getMinutes(),
      startTime.getSeconds(),
      startTime.getMilliseconds()
    )
    return result
  }

  // fallback if startTime is returned as string like "14:00:00"
  const [hours, minutes, seconds = '0'] = startTime.split(':')
  result.setHours(Number(hours), Number(minutes), Number(seconds), 0)

  return result
}

export const isEmpty = (value: unknown) => {
  if (
    value === '' ||
    value === undefined ||
    value === null ||
    Number.isNaN(value) ||
    JSON.stringify(value) === '{}'
  )
    return true;
  return false;
};

export function removeUndefined<T extends Record<string, any>>(obj: T) {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, v]) => v !== undefined)
  )
}

export function getCurrentLocation(): Promise<{ lat: number; lng: number }> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'))
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (error) => reject(error),
      {
        enableHighAccuracy: true, // more precise
        timeout: 10000,           // 10 seconds
        maximumAge: 0,
      }
    )
  })
}